import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import { useAuth } from '../auth/useAuth'
import ErrorAlert from '../components/ErrorAlert'
import KpiGrid from '../components/KpiGrid'
import type { Advisor, Incidencia, IncidenciasResponse, Severidad } from '../types/portal'
import { QUE_ES_UN_CLIENTE_ESPERANDO } from '../sla'

/**
 * «Panel» — lo que necesita una persona AHORA. Es la entrada de supervisión y
 * de Admin.
 *
 * Reemplaza al Dashboard, que respondía otra pregunta —«¿cuánto hay?»— y
 * encima con datos inventados: «Disponibilidad 99.94 %» y seis servicios en
 * «Operativo» estaban escritos en el código, y costos y escalamiento salían
 * siempre en «—». Un tablero que dice que todo anda sin mirarlo es peor que
 * ninguno: entrena a no creerle.
 *
 * Arriba va la respuesta a «¿hay algo que hacer?»; después los números, y
 * después el detalle. Todo sale de endpoints que ya existen y que alimentan
 * otras pantallas, así que el panel no puede contradecirlas:
 *
 *   /conversations/pending-count  los mismos contadores del sidebar
 *   /incidents                    alarmas, colas de error, mensajes no
 *                                 entregados, documentos trabados, clientes
 *                                 esperando y reportes de los asesores
 *   /advisors                     quién está recibiendo
 *   /dashboard                    tiempo de respuesta, tokens y outreach
 */

const POLL_MS = 30_000

interface Pendientes {
  pending_unassigned?: number
  docs_pending_review?: number
}

interface DashboardData {
  conversations?: { active: number; total: number }
  outreach?: { daily_sent: number; daily_limit: number }
  model_calls?: { today: number; budget_tokens: number }
  respuesta_documentos?: { p50: number | null; p90: number | null; max: number | null
                           n: number; dias: number }
}

type Tono = 'danger' | 'warn' | 'neutral'

interface Pendiente {
  clave: string
  tono: Tono
  titulo: string
  detalle: string
  /** Dónde se resuelve. */
  to: string
  origen?: string
}

const TONO_POR_SEVERIDAD: Record<Severidad, Tono> = {
  CRITICA: 'danger', ALTA: 'danger', MEDIA: 'warn', BAJA: 'neutral',
}

const PUNTO: Record<Tono, string> = {
  danger: 'bg-danger',
  warn: 'bg-warn-dot',
  neutral: 'bg-ink-ghost',
}

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`
}

function segundos(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${v.toFixed(1)} s`
}

/** La conversación, cuando la incidencia cuelga de una; si no, la lista. Las
 *  de una PERSONA (`asesor-pausa:` / `asesor-espera:`, DEF-007) llevan a
 *  Asesores, que es donde se ve con qué cuenta cada uno y desde donde se
 *  decide a quién reasignar. */
function destinoDe(i: Incidencia): string {
  if (i.conversation_id) return `/conversations/${i.conversation_id}`
  return i.id.startsWith('asesor-') ? '/advisors' : '/incidents'
}

export default function Panel() {
  const { role } = useAuth()
  const esAdmin = role === 'admin'

  const [pend, setPend] = useState<Pendientes | null>(null)
  const [inc, setInc] = useState<IncidenciasResponse | null>(null)
  const [asesores, setAsesores] = useState<Advisor[]>([])
  const [dash, setDash] = useState<DashboardData | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    // `allSettled`: si una fuente falla, el resto del panel sigue sirviendo.
    // Sin las alarmas, saber que hay tres clientes esperando sigue valiendo.
    const [p, i, a, d] = await Promise.allSettled([
      apiClient.get<Pendientes>('/conversations/pending-count'),
      apiClient.get<IncidenciasResponse>('/incidents'),
      apiClient.get<{ advisors: Advisor[] }>('/advisors'),
      apiClient.get<DashboardData>('/dashboard'),
    ])
    // Las dos fuentes de «¿hay algo que hacer?» se vacían si fallan, en vez de
    // quedarse con la lectura anterior: un panel que dice «al día» con datos de
    // hace diez minutos es el mismo error que el Dashboard con sus «Operativo».
    setPend(p.status === 'fulfilled' ? p.value.data : null)
    setInc(i.status === 'fulfilled' ? i.value.data : null)
    if (a.status === 'fulfilled') setAsesores(a.value.data.advisors ?? [])
    if (d.status === 'fulfilled') setDash(d.value.data)
    const fallo = [p, i, a, d].find((r) => r.status === 'rejected')
    setError(fallo && fallo.status === 'rejected'
      ? errorMessage(fallo.reason, 'Parte del panel no se pudo cargar.')
      : null)
    setCargando(false)
  }, [])

  useEffect(() => {
    void cargar()
    const id = setInterval(() => { void cargar() }, POLL_MS)
    return () => clearInterval(id)
  }, [cargar])

  const abiertas = useMemo(
    () => (inc?.incidencias ?? []).filter((i) => i.estado !== 'RESUELTA'),
    [inc],
  )

  // Lo que una persona tiene que hacer, en el orden en que conviene hacerlo:
  // primero lo crítico, después la cola y los documentos, después el resto.
  const pendientes = useMemo<Pendiente[]>(() => {
    const cola = pend?.pending_unassigned ?? 0
    const docs = pend?.docs_pending_review ?? 0
    const propios: Pendiente[] = []
    if (cola) propios.push({
      clave: 'cola', tono: 'warn', to: '/queue',
      titulo: plural(cola, 'conversación sin asignar', 'conversaciones sin asignar'),
      detalle: 'Esperan que un asesor las tome desde la cola global.',
    })
    if (docs) propios.push({
      clave: 'docs', tono: 'warn', to: '/documents',
      titulo: plural(docs, 'documento por revisar', 'documentos por revisar'),
      detalle: 'El agente no pudo confirmar el tipo con suficiente certeza.',
    })
    const deIncidencias = abiertas.map<Pendiente>((i) => ({
      clave: i.id, tono: TONO_POR_SEVERIDAD[i.severidad], to: destinoDe(i),
      titulo: i.titulo,
      detalle: [i.afectado, i.afectado_detalle].filter(Boolean).join(' · '),
      origen: i.origen,
    }))
    const criticas = deIncidencias.filter((x) => x.tono === 'danger')
    const resto = deIncidencias.filter((x) => x.tono !== 'danger')
    return [...criticas, ...propios, ...resto]
  }, [pend, abiertas])

  const k = inc?.kpis
  const rd = dash?.respuesta_documentos
  const hayCriticas = pendientes.some((p) => p.tono === 'danger')
  // Sin alguna de las dos fuentes no se puede afirmar que no hay nada: la
  // lista muestra lo que sí se leyó y el titular no dice «al día».
  const sinLeer = !pend || !inc
  const tonoTarjeta = hayCriticas ? 'border-l-danger'
    : pendientes.length > 0 || sinLeer ? 'border-l-warn-dot' : 'border-l-brand'

  if (cargando && !pend && !inc) {
    return <p className="text-body text-ink-muted">Cargando el panel…</p>
  }

  return (
    <div className="flex flex-col gap-5">
      {error && <ErrorAlert message={error} onRetry={() => void cargar()} />}

      {/* ── ¿Hay algo que hacer? ─────────────────────────────────────── */}
      <section className={`card border-l-4 p-5 ${tonoTarjeta}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-card font-extrabold text-ink">
              {pendientes.length > 0
                ? plural(pendientes.length, 'cosa necesita una persona',
                         'cosas necesitan una persona')
                : sinLeer ? 'No se pudo confirmar el estado de la operación'
                  : 'La operación está al día'}
            </h2>
            <p className="mt-0.5 text-micro text-ink-muted">
              {sinLeer
                ? `No se pudo leer ${!inc ? 'Incidencias' : 'la cola y los documentos'}; lo de abajo puede estar incompleto.`
                : pendientes.length === 0
                  ? 'Sin conversaciones esperando, sin documentos por revisar y sin incidencias abiertas.'
                  : 'Lo más urgente primero. Cada fila lleva a donde se resuelve.'}
            </p>
          </div>
          <Link to="/queue" className="btn-ghost shrink-0">Abrir la cola</Link>
        </div>

        {pendientes.length > 0 && (
          <ul className="mt-4 flex flex-col divide-y divide-surface-faint">
            {pendientes.slice(0, 6).map((p) => (
              <li key={p.clave}>
                <Link to={p.to}
                      className="flex items-start gap-3 rounded-field px-2 py-2.5 hover:bg-surface-alt">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${PUNTO[p.tono]}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-body font-bold text-ink">{p.titulo}</span>
                    {p.detalle && (
                      <span className="block truncate text-micro text-ink-muted">{p.detalle}</span>
                    )}
                  </span>
                  {p.origen && (
                    <span className="badge-gray shrink-0">{p.origen}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
        {pendientes.length > 6 && (
          <Link to="/incidents" className="mt-2 inline-block text-micro font-bold text-info-ink hover:underline">
            Ver las {pendientes.length} en Incidencias
          </Link>
        )}
      </section>

      {/* ── Los números ──────────────────────────────────────────────── */}
      <KpiGrid
        kpis={[
          { label: 'Sin asignar', value: pend?.pending_unassigned ?? '—',
            hint: 'En la cola global',
            tone: (pend?.pending_unassigned ?? 0) > 0 ? 'warn' : 'default' },
          // DEF-112: la misma regla que En vivo y Asesores, dicha acá.
          { label: 'Clientes esperando', value: k?.clientes_esperando ?? '—',
            hint: QUE_ES_UN_CLIENTE_ESPERANDO,
            tone: (k?.clientes_esperando ?? 0) > 0 ? 'danger' : 'default' },
          { label: 'Documentos por revisar', value: pend?.docs_pending_review ?? '—',
            hint: 'Tipo sin confirmar',
            tone: (pend?.docs_pending_review ?? 0) > 0 ? 'warn' : 'default' },
          { label: 'Respuesta a documentos', value: segundos(rd?.p50),
            hint: rd?.n ? `p50 · p90 ${segundos(rd.p90)} · ${rd.n} respuestas en ${rd.dias} días`
                        : 'Sin datos todavía' },
        ]}
      />

      {/* ── Quién está atendiendo ────────────────────────────────────── */}
      <section className="card p-5">
        <div className="flex items-baseline justify-between">
          <h2 className="section-title">Asesores</h2>
          <Link to="/advisors" className="text-micro font-bold text-info-ink hover:underline">
            Ver la rotación
          </Link>
        </div>
        {asesores.length === 0 ? (
          <p className="mt-3 text-body text-ink-muted">
            No hay asesores en el grupo Asesor. Se agregan desde Administración › Usuarios.
          </p>
        ) : (
          <ul className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {asesores.map((a) => (
              <li key={a.email}
                  className="flex items-center justify-between gap-3 rounded-field border border-surface-border px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-body font-bold text-ink">{a.name}</span>
                  <span className="block text-micro text-ink-muted">
                    {/* DEF-112: es el mismo número que Asesores llama «En manos de
                        una persona»; «activas» se leía como las «en curso» de
                        En vivo, que cuenta otra cosa (todo lo vivo, bot incluido). */}
                    {a.active_conversations} en manos de una persona
                  </span>
                </span>
                <span className={a.available ? 'badge-green' : 'badge-amber'}>
                  {a.available ? 'Recibiendo' : 'En pausa'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Salud del sistema (Admin) ────────────────────────────────── */}
      {esAdmin && (
        <section className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <h2 className="section-title">Salud del sistema</h2>
            <Link to="/incidents" className="text-micro font-bold text-info-ink hover:underline">
              Ver Observabilidad
            </Link>
          </div>
          <KpiGrid
            kpis={[
              saludDe('Alarmas de CloudWatch',
                      inc ? abiertas.filter((i) => i.origen === 'CloudWatch').length : undefined,
                      'en alarma', 'Ninguna en alarma'),
              saludDe('Colas de error', k?.en_dlq, 'con mensajes', 'Vacías'),
              saludDe('Mensajes no entregados', k?.mensajes_no_entregados,
                      'rechazados por Meta', 'Todos entregados'),
              saludDe('Documentos sin procesar', k?.docs_no_procesados,
                      'trabados en el pipeline', 'Pipeline al día'),
            ]}
          />
          <KpiGrid
            kpis={[
              // Desde que el cliente no espera a Drive, un documento sin
              // archivar no se nota en el chat: este número es donde se ve.
              saludDe('Archivado en Drive', k?.drive_sin_archivar,
                      'sin archivar o sin conexión', 'Todo archivado'),
              { label: 'Conversaciones activas', value: dash?.conversations?.active ?? '—',
                hint: `Con movimiento en 24 h · ${dash?.conversations?.total ?? '—'} en total` },
              { label: 'Tokens hoy',
                value: dash?.model_calls ? `${(dash.model_calls.today / 1000).toFixed(1)}K` : '—',
                hint: dash?.model_calls
                  ? `de ${(dash.model_calls.budget_tokens / 1_000_000).toFixed(0)}M de presupuesto`
                  : undefined },
              { label: 'Outreach hoy',
                value: dash?.outreach ? `${dash.outreach.daily_sent} / ${dash.outreach.daily_limit}` : '—',
                hint: 'Mensajes salientes del día' },
            ]}
          />
        </section>
      )}
    </div>
  )
}

/** Una tarjeta de salud: el número si hay algo, la frase tranquila si no. */
function saludDe(label: string, n: number | undefined, conProblema: string,
                 sinProblema: string) {
  if (n === undefined) return { label, value: '—', hint: 'No se pudo leer' }
  return n > 0
    ? { label, value: n, hint: conProblema, tone: 'danger' as const }
    : { label, value: 'OK', hint: sinProblema }
}
